import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Material UI
import Paper from '@mui/material/Paper';

// Import our store

// API
import {getJobStatus, JobStatus, is_JobRecord, JobRecord, BackendMetadataRecord, getBackendMetadata } from '../api/api';
import ThreeColumnContent from '../components/core/ThreeColumnContent';
import JobStatusChip from '../components/results/JobStatusChip';
import Navbar from '../components/core/Navbar';
import DownloadResults from '../components/results/DownloadResults';
import { getJob, removeJob, updateJob } from '../storage/MySearchesStore';
import VisualizeCSV from '../components/results/VisualizeCSV';
import Footer from '../components/core/Footer';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import StandardView from '../components/core/StandardView';
import SearchCriteria from '../components/results/SearchCritera';
import { Species } from '../Types';
import { usePolling } from '../hooks/usePollingRate';

// Types
interface Props {

}

// Constants
const AVERAGE_MINUTES_PER_JOB = 2;
const POLLING_RATE_SECONDS = 10;

const ResultsPage = ({}: Props) => {

  // We want to be able to pull out the id of the search request from the URL. 
  // We can do this with the useParams hook provided by react-router-dom
  let { id } = useParams();

  // Use the useNavigate Hook to be able to navigate to different routes.
  const navigate = useNavigate(); 

  // We want to store information about this job in the local state of this component.
  const [job, setJob] = useState<JobRecord | null>(null);

  const searchCriteria = useMemo(() => {
    return getJob(id)
  }, [id])

  const [hasResults, setHasResults] = useState<boolean>(false);

  // Determine how many jobs can run concurrently
  const [metadata, setMetadata] = React.useState<BackendMetadataRecord>();
  useEffect(() => {
    getBackendMetadata().then((data) => {
      setMetadata(data as BackendMetadataRecord);
    })
  }, []);

  usePolling(() => {
    console.log("Query")
    // Check our job status
    checkJobStatus(id).then((jobStatus : JobStatus) => {
      // If we get back a non-running value end
      if ((jobStatus === JobStatus.RUNNING) || (jobStatus === JobStatus.QUEUED)) {
        // We want to keep Querying at this interval such that the UI updates to reflect the status of this Job
      } else {
        // We have a result
        console.log("Result", jobStatus);
        setHasResults(true);
      }
    })
  }, POLLING_RATE_SECONDS * 1000, !hasResults);

  const checkJobStatus = (id : string | undefined) : Promise<JobStatus> => {
    return new Promise((resolve, reject) => {
      // If we have an ID
      if(id){
        getJobStatus(id).then((data) => {
          // If this code block triggers, we got back data from the API.
          // Check to see if we got an instance of a JobRecord
          if(is_JobRecord(data)){
            // If we got a JobRecord, cast the data object to this type so we can resolve type-members.
            let jobRecord : JobRecord = data as JobRecord;
            // Now that we know the status of this job, set the status state-variable
            setJob(jobRecord);
            // Lets also update this in our cache because we have just retrieved information about this job that is more accurate than the data in our cache.
            updateJob(jobRecord);
            // Resolve
            resolve(jobRecord.status);
          } else {
            // If we get here, we did not find a job with the given ID. 
            navigate("/search_not_found/" + id);
            // Reject
            reject();
          }
        }).catch((err) => {
          //TODO: Maybe communicate that this is an API issue.
          // If this code block triggers, an error happened.
          console.log("Error:", err);
          // Now that we have our response, navigate to the search results view for this job.
          // navigate("/api_communication_issue");
          navigate("/search_not_found/" + id);
          // Reject
          reject();
        })
      } else {
        // No ID passed, so redirect to search.
        navigate("/search");
        // Reject
        reject();
      }
    });
  }

  const jobHasFinished = useMemo(() => {
    if(!job){
      return false
    }
    return job.status == JobStatus.FINISHED_WITH_RESULTS
  }, [job])

  const determineSearchMessage = () => {
    // If we dont have a job
    if (!job) {
      return <CircularProgress size={100} />
    }

    // If we are waiting for our job to start
    if (job.status === JobStatus.QUEUED) {
      const maxConcurrent = metadata?.maxConcurrentJobs || 1;
      const estimatedMinutes = Math.max(Math.ceil(((job.positionInJobQueue - 1) / maxConcurrent) * AVERAGE_MINUTES_PER_JOB), 1);

      return (
        <Stack
          p={4}
          gap={3}
          direction="column"
          alignItems="center"
          justifyContent="center"
          maxWidth="500px"
          textAlign="center"
        >
          <Typography variant="h3" component="h3">
            Waiting to Start
          </Typography>

          <Typography variant="h4" component="h4" color="primary">
            Queue Position: {job.positionInJobQueue}
          </Typography>

          <Typography variant="body1" component="p">
            Our system can only run a limited number of searches at a time. Currently, the servers are at capacity.
            No need to refresh — the page will update automatically as your position in the queue changes.
          </Typography>

          <Typography variant="body2" component="p" color="textSecondary">
            Estimated time until your job starts: ~{estimatedMinutes} minute{estimatedMinutes > 1 ? 's' : ''}
          </Typography>

          <CircularProgress size={80} />
        </Stack>
      );
    }

    if (job!.status === JobStatus.RUNNING) {
      return (
        <Stack
          p={4}
          gap={3}
          direction="column"
          alignItems="center"
          justifyContent="center"
          maxWidth="500px"
          textAlign="center"
        >
          <Typography variant="h3" component="h3">
            Searching for siRNA
          </Typography>
          <Typography variant="h5" component="h5">
            Your search is currently in progress.
          </Typography>
          <Typography variant="h6" component="h6">
            We're gathering your results now. This typically takes around {AVERAGE_MINUTES_PER_JOB} minutes.  
            No need to refresh — the page will update automatically once your results are ready.
          </Typography>
          <CircularProgress size={100} />
        </Stack>
      );
    }

    // If our job has started and is running
    return (
      <Stack
        p={4}
        gap={3}
        direction="column"
        alignItems="center"
        justifyContent="center"
        maxWidth="500px"
        textAlign="center"
      >
        <Typography variant="h3" component="h3">
          Something Went Wrong
        </Typography>
        <Typography variant="body1" component="p">
          We couldn’t locate this job. It might have been deleted from our servers, or there was an error processing your search.  
          Please try running your search again.
        </Typography>
        <Button variant='contained' onClick={() => {
          navigate("/");
        }}>New Search</Button>
      </Stack>
    );
  };

  return (
    <StandardView>
      {/* If we are in the queue, or the job is in progress */}
      {!jobHasFinished && <Box 
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto'
        }}
      >
        <Paper elevation={10}>
          <Stack direction={'column'} alignItems={'center'} p={4}>
            {determineSearchMessage()}
          </Stack>
        </Paper>
      </Box>}

      {/* Results */}
      {jobHasFinished && <VisualizeCSV jobID={id!}/>}
    </StandardView>
  );
};

export default ResultsPage;